import { useEffect, useState } from "react"

import {
  AI_ACTION_VALUES,
  AskQuestionEvalMock,
  AskQuestionEvalMockCase,
  AskQuestionEvalMockInput,
  ESCALATING_AI_ACTIONS,
  createAiEvalMock,
  updateAiEvalMock
} from "@domain/ai_eval_mocks"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  initial?: AskQuestionEvalMock | null
}

type CaseFormState = {
  query: string
  expectedContentIncludes: string[]
  expectedAiAction: string
  chipInputValue: string
}

type FormState = {
  title: string
  cases: CaseFormState[]
}

function defaultCase(): CaseFormState {
  return {
    query: "",
    expectedContentIncludes: [],
    expectedAiAction: "",
    chipInputValue: ""
  }
}

function defaultState(): FormState {
  return { title: "", cases: [defaultCase()] }
}

function hydrate(mock: AskQuestionEvalMock): FormState {
  return {
    title: mock.title,
    cases:
      mock.cases?.length > 0
        ? mock.cases.map((c) => ({
            query: c.query,
            expectedContentIncludes:
              c.assertions?.expectedContentIncludes || [],
            expectedAiAction: c.assertions?.expectedAiAction || "",
            chipInputValue: ""
          }))
        : [defaultCase()]
  }
}

export function MockFormDialog({ open, onClose, onSaved, initial }: Props) {
  const isEdit = Boolean(initial)
  const [state, setState] = useState<FormState>(defaultState())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) setState(initial ? hydrate(initial) : defaultState())
  }, [open, initial])

  const updateCase = (index: number, patch: Partial<CaseFormState>) =>
    setState((s) => {
      const cases = [...s.cases]
      cases[index] = { ...cases[index], ...patch }
      return { ...s, cases }
    })

  const addCase = () =>
    setState((s) => ({ ...s, cases: [...s.cases, defaultCase()] }))

  const removeCase = (index: number) =>
    setState((s) => ({ ...s, cases: s.cases.filter((_, i) => i !== index) }))

  const buildPayloadCases = (): AskQuestionEvalMockCase[] =>
    state.cases.map((c) => {
      const includes = [...c.expectedContentIncludes]
      const trimmed = c.chipInputValue.trim()
      if (trimmed && !includes.includes(trimmed)) includes.push(trimmed)
      return {
        query: c.query.trim(),
        assertions: {
          expectedContentIncludes: includes,
          expectedAiAction: c.expectedAiAction || undefined
        }
      }
    })

  const validate = (): string | null => {
    if (!state.title.trim()) return "Başlık boş olamaz"
    if (state.cases.length === 0) return "En az 1 test case ekleyin"
    if (state.cases.some((c) => !c.query.trim()))
      return "Her case için soru girilmelidir"
    return null
  }

  const handleSave = async () => {
    const err = validate()
    if (err) {
      showErrorToast(err)
      return
    }
    setSaving(true)
    try {
      const payload: AskQuestionEvalMockInput = {
        title: state.title.trim(),
        cases: buildPayloadCases()
      }
      const res = isEdit
        ? await updateAiEvalMock((initial as AskQuestionEvalMock)._id, payload)
        : await createAiEvalMock(payload)

      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || "Senaryo kaydedilemedi")
      } else {
        showSuccessToast(
          isEdit ? "Test senaryosu güncellendi" : "Test senaryosu oluşturuldu"
        )
        onSaved()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {isEdit ? "Test Senaryosu Düzenle" : "Yeni Test Senaryosu"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Her case için ayrı soru ve beklenen sonuç tanımlayabilirsin. Pipeline
          çalıştırma sırasında seçilir.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Temel Bilgiler */}
        <Box sx={{ p: 3 }}>
          <SectionLabel>Temel Bilgiler</SectionLabel>
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <TextField
              label="Başlık"
              fullWidth
              size="small"
              value={state.title}
              onChange={(e) =>
                setState((s) => ({ ...s, title: e.target.value }))
              }
              placeholder="ör. Kargo bilgisi soruları"
            />
          </Stack>
        </Box>

        <Divider />

        {/* Test Cases */}
        <Box sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <SectionLabel>Test Cases ({state.cases.length})</SectionLabel>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addCase}
              variant="outlined"
              disableElevation
              sx={{ fontSize: 12 }}
            >
              Case Ekle
            </Button>
          </Stack>

          <Stack spacing={2}>
            {state.cases.map((c, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#fff", fontWeight: 700, lineHeight: 1 }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      Case #{index + 1}
                    </Typography>
                  </Stack>
                  {state.cases.length > 1 && (
                    <Tooltip title="Bu case'i sil">
                      <IconButton
                        size="small"
                        onClick={() => removeCase(index)}
                        sx={{
                          color: "text.disabled",
                          ":hover": { color: "error.main" }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>

                <Stack spacing={1.5}>
                  <TextField
                    label="Müşteri Sorusu"
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={3}
                    value={c.query}
                    onChange={(e) =>
                      updateCase(index, { query: e.target.value })
                    }
                    placeholder="ör. Hangi kargo ile çalışıyorsunuz?"
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Autocomplete
                      multiple
                      freeSolo
                      autoSelect
                      size="small"
                      options={[]}
                      value={c.expectedContentIncludes}
                      inputValue={c.chipInputValue}
                      onInputChange={(_, value) =>
                        updateCase(index, { chipInputValue: value })
                      }
                      onChange={(_, value) =>
                        updateCase(index, {
                          expectedContentIncludes: value as string[],
                          chipInputValue: ""
                        })
                      }
                      sx={{ flex: 1 }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, i) => (
                          <Chip
                            {...getTagProps({ index: i })}
                            key={`${option}-${i}`}
                            label={option}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Cevapta geçmesi gereken kelimelerden en az biri"
                          placeholder="Kelime yaz, Enter"
                          helperText="Herhangi biri geçerse yeterli. Boş = kontrol edilmez"
                        />
                      )}
                    />

                    <TextField
                      select
                      label="Beklenen Aksiyon"
                      size="small"
                      value={c.expectedAiAction}
                      onChange={(e) =>
                        updateCase(index, { expectedAiAction: e.target.value })
                      }
                      sx={{ minWidth: 180 }}
                      helperText={
                        ESCALATING_AI_ACTIONS.includes(
                          c.expectedAiAction as never
                        )
                          ? "Finalize adımı çalışmaz, otomatik hariç tutulur"
                          : "Boş = kontrol edilmez"
                      }
                    >
                      <MenuItem value="">— Kontrol etme —</MenuItem>
                      {AI_ACTION_VALUES.map((action) => (
                        <MenuItem key={action} value={action}>
                          {action}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          disableElevation
        >
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
    >
      {children}
    </Typography>
  )
}
